import React from 'react'
import './LoadingStatus.css'

export type LoadingStage = 'downloading' | 'assembling' | 'parsing' | 'rendering' | 'uploading'

export interface DownloadProgress {
  stage: LoadingStage
  percentage: number
  currentChunk?: number
  totalChunks?: number
  bytesDownloaded?: number
  totalBytes?: number
  downloadSpeed?: number // bytes per second
  estimatedTimeRemaining?: number // seconds
}

export interface UploadProgress {
  stage: 'uploading'
  percentage: number
  bytesUploaded?: number
  totalBytes?: number
  uploadSpeed?: number // bytes per second
  estimatedTimeRemaining?: number // seconds
}

interface LoadingStatusProps {
  isLoading: boolean
  message: string
  details?: string[]
  elapsedSeconds?: number
  progress?: number // 0-100, optional (legacy support)
  downloadProgress?: DownloadProgress // Enhanced progress tracking
  uploadProgress?: UploadProgress // Upload progress tracking
  error?: string | null
  onCancel?: () => void
  onErrorClose?: () => void // Callback to close error message
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const formatTime = (seconds: number): string => {
  if (seconds < 60) return `~${Math.round(seconds)}s`
  const minutes = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return `~${minutes}m ${secs}s`
}

const getStageInfo = (stage: LoadingStage) => {
  switch (stage) {
    case 'downloading':
      return { icon: '📥', label: 'Downloading', color: '#007acc' }
    case 'uploading':
      return { icon: '📤', label: 'Uploading', color: '#27ae60' }
    case 'assembling':
      return { icon: '🔧', label: 'Assembling', color: '#ff8c00' }
    case 'parsing':
      return { icon: '📐', label: 'Parsing', color: '#9b59b6' }
    case 'rendering':
      return { icon: '🎨', label: 'Rendering', color: '#28a745' }
  }
}

export const LoadingStatus: React.FC<LoadingStatusProps> = ({
  isLoading,
  message,
  details,
  elapsedSeconds,
  progress,
  downloadProgress,
  uploadProgress,
  error,
  onCancel,
  onErrorClose
}) => {
  if (!isLoading && !error) return null

  const progressData = uploadProgress || downloadProgress
  const stageInfo = progressData ? getStageInfo(progressData.stage) : null

  return (
    <div className="loading-status-overlay">
      <div className="loading-status-modal">
        {error ? (
          <>
            {/* Close button for error */}
            {onErrorClose && (
              <button
                className="error-close-button"
                onClick={onErrorClose}
                aria-label="Close error message"
              >
                ×
              </button>
            )}
            <div className="loading-status-icon error">❌</div>
            <h3 className="loading-status-title">Error</h3>
            <p className="loading-status-message">{error}</p>
          </>
        ) : progressData ? (
          <>
            {/* Multi-stage progress display */}
            <div className="loading-status-icon" style={{ color: stageInfo?.color }}>
              {stageInfo?.icon}
            </div>
            <h3 className="loading-status-title">{uploadProgress ? 'Uploading STL File' : 'Loading STL File'}</h3>

            {/* Stage indicator */}
            <div className="stage-indicator">
              <span className="stage-label" style={{ color: stageInfo?.color }}>
                {stageInfo?.label}
              </span>
            </div>

            {/* Stage-specific details */}
            {progressData.stage === 'downloading' && downloadProgress && (
              <div className="download-details">
                {downloadProgress.currentChunk !== undefined && downloadProgress.totalChunks !== undefined && (
                  <p className="detail-text">
                    Chunk {downloadProgress.currentChunk + 1} of {downloadProgress.totalChunks}
                  </p>
                )}
                {downloadProgress.bytesDownloaded !== undefined && downloadProgress.totalBytes !== undefined && (
                  <p className="detail-text">
                    {formatBytes(downloadProgress.bytesDownloaded)} / {formatBytes(downloadProgress.totalBytes)}
                  </p>
                )}
                {downloadProgress.downloadSpeed !== undefined && downloadProgress.downloadSpeed > 0 && (
                  <p className="detail-text speed">
                    {formatBytes(downloadProgress.downloadSpeed)}/s
                  </p>
                )}
                {downloadProgress.estimatedTimeRemaining !== undefined && downloadProgress.estimatedTimeRemaining > 0 && (
                  <p className="detail-text time-remaining">
                    {formatTime(downloadProgress.estimatedTimeRemaining)} remaining
                  </p>
                )}
              </div>
            )}

            {progressData.stage === 'uploading' && uploadProgress && (
              <div className="upload-details">
                {uploadProgress.bytesUploaded !== undefined && uploadProgress.totalBytes !== undefined && (
                  <p className="detail-text">
                    {formatBytes(uploadProgress.bytesUploaded)} / {formatBytes(uploadProgress.totalBytes)}
                  </p>
                )}
                {uploadProgress.uploadSpeed !== undefined && uploadProgress.uploadSpeed > 0 && (
                  <p className="detail-text speed">
                    {formatBytes(uploadProgress.uploadSpeed)}/s
                  </p>
                )}
                {uploadProgress.estimatedTimeRemaining !== undefined && uploadProgress.estimatedTimeRemaining > 0 && (
                  <p className="detail-text time-remaining">
                    {formatTime(uploadProgress.estimatedTimeRemaining)} remaining
                  </p>
                )}
              </div>
            )}

            {downloadProgress && downloadProgress.stage === 'assembling' && downloadProgress.totalChunks && (
              <p className="loading-status-message">
                Assembling {downloadProgress.totalChunks} chunks into complete file...
              </p>
            )}

            {downloadProgress && downloadProgress.stage === 'parsing' && (
              <p className="loading-status-message">
                Parsing STL geometry...
              </p>
            )}

            {downloadProgress && downloadProgress.stage === 'rendering' && (
              <p className="loading-status-message">
                Creating 3D mesh...
              </p>
            )}

            {/* Progress bar */}
            <div className="loading-status-progress">
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${progressData.percentage}%`,
                    backgroundColor: stageInfo?.color
                  }}
                ></div>
              </div>
              <span className="progress-text">{Math.round(progressData.percentage)}%</span>
            </div>

            {/* Cancel button */}
            {onCancel && downloadProgress && downloadProgress.stage === 'downloading' && (
              <button className="cancel-button" onClick={onCancel}>
                Cancel Download
              </button>
            )}

            {details && details.length > 0 && (
              <div className="loading-status-details">
                {details.map((detail, index) => (
                  <p key={`${detail}-${index}`} className="loading-status-detail-line">{detail}</p>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Spinner display */}
            <div className="loading-status-icon">
              <div className="spinner"></div>
            </div>
            <h3 className="loading-status-title">{message}</h3>

            {elapsedSeconds !== undefined && elapsedSeconds >= 0 && (
              <div className="elapsed-timer">
                Elapsed: {formatTime(elapsedSeconds)}
              </div>
            )}

            {progress !== undefined && (
              <div className="loading-status-progress">
                <div className="progress-bar-container">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{Math.round(progress)}%</span>
              </div>
            )}

            {details && details.length > 0 && (
              <div className="loading-status-details">
                {details.map((detail, index) => (
                  <p key={`${detail}-${index}`} className="loading-status-detail-line">{detail}</p>
                ))}
              </div>
            )}

            {/* Cancel button (server-side generation in flight) */}
            {onCancel && (
              <button className="cancel-button" onClick={onCancel}>
                Cancel Generation
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

